import {degreesToRadians} from '@Utils/math.util';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {ItemLoadingState} from '@Components/poster-editor/poster-editor-reducer';
import type {LoadingIndicatorPosition} from './item-loading-indicators.types';
import {
  LOADING_INDICATOR_MARGIN_FROM_EDGE,
  LOADING_INDICATOR_HEIGHT,
  LOADING_INDICATOR_PADDING,
  LOADING_INDICATOR_TEXT_GAP,
  LOADING_INDICATOR_CIRCULAR_PROGRESS_DIAMETER,
  LOADING_INDICATOR_POSITION_BUFFER,
  LOADING_INDICATOR_TEXT_FIT_BUFFER,
} from './item-loading-indicators.types';

export interface UseItemLoadingLayoutParams {
  item: ItemType;
  loadingState: ItemLoadingState;
  fitToScreenScale: number;
  posterScale: number;
  textMeasurementCanvas: CanvasRenderingContext2D | null;
}

export interface ItemLoadingLayout {
  loadingText: string;
  position: LoadingIndicatorPosition;
  width: number;
}

export function useLoadingIndicatorLayout({
  item,
  loadingState,
  fitToScreenScale,
  posterScale,
  textMeasurementCanvas,
}: UseItemLoadingLayoutParams): ItemLoadingLayout | null {
  const getLoadingWidthCanvas = (text: string | undefined): number => {
    const circularProgressDiameterCanvas = LOADING_INDICATOR_CIRCULAR_PROGRESS_DIAMETER / fitToScreenScale;
    const textGapCanvas = LOADING_INDICATOR_TEXT_GAP / fitToScreenScale;
    const loadingPaddingCanvas = LOADING_INDICATOR_PADDING / fitToScreenScale;

    let textWidthCanvas = 0;
    if (text && textMeasurementCanvas) {
      const textScale = 1 / fitToScreenScale;
      const baseWidth = textMeasurementCanvas.measureText(text).width;
      textWidthCanvas = baseWidth * textScale; // Canvas space only
    }

    if (!text) {
      return circularProgressDiameterCanvas + loadingPaddingCanvas * 2;
    }
    return circularProgressDiameterCanvas + textGapCanvas + textWidthCanvas + loadingPaddingCanvas * 2;
  };

  const getLoadingWidthViewport = (text: string | undefined): number => {
    return getLoadingWidthCanvas(text) * posterScale;
  };

  const getLoadingText = (item: ItemType, loadingState: ItemLoadingState): string => {
    if (loadingState.hideText) {
      return '';
    }
    const percentage = Math.round(loadingState.progress * 100);
    const margin = LOADING_INDICATOR_MARGIN_FROM_EDGE / fitToScreenScale;
    const buffer = LOADING_INDICATOR_TEXT_FIT_BUFFER / fitToScreenScale;
    const itemAbsoluteScaledWidth = item.getFabricObjectAbsoluteScaledWidth();

    if (loadingState.langTextKeys && loadingState.langTextKeys.length > 0) {
      for (const langKey of loadingState.langTextKeys) {
        const text = window.i18next.t(langKey, {percentage});
        const loadingWidth = getLoadingWidthCanvas(text);

        if (loadingWidth + margin + buffer <= itemAbsoluteScaledWidth) {
          return text;
        }
      }

      if (loadingState.progressType !== undefined) {
        return window.i18next.t('pmwjs_percentage', {percentage});
      }
    }

    if (loadingState.progressType !== undefined) {
      return window.i18next.t('pmwjs_percentage', {percentage});
    }

    return window.i18next.t('pmwjs_loading');
  };

  const getLoadingPosition = (item: ItemType, loadingText: string): LoadingIndicatorPosition | null => {
    if (!item.fabricObject) {
      return null;
    }

    const itemAbsLeft = item.getFabricObjectAbsoluteLeft();
    const itemAbsTop = item.getFabricObjectAbsoluteTop();
    const itemAbsoluteAngle = item.getFabricObjectAbsoluteAngle();
    const itemAbsoluteScaledWidth = item.getFabricObjectAbsoluteScaledWidth();
    const itemAbsoluteScaledHeight = item.getFabricObjectAbsoluteScaledHeight();

    const actualLoadingWidth = getLoadingWidthCanvas(loadingText);
    const loadingHeightCanvas = LOADING_INDICATOR_HEIGHT / fitToScreenScale;
    const margin = LOADING_INDICATOR_MARGIN_FROM_EDGE / fitToScreenScale;

    let relativeX = itemAbsoluteScaledWidth - actualLoadingWidth - margin;
    let relativeY = itemAbsoluteScaledHeight - loadingHeightCanvas - margin;

    const angleRadians = degreesToRadians(itemAbsoluteAngle);
    let angleAdjustedRelativeX = relativeX * Math.cos(angleRadians) - relativeY * Math.sin(angleRadians);
    let angleAdjustedRelativeY = relativeX * Math.sin(angleRadians) + relativeY * Math.cos(angleRadians);

    let loadingAbsLeft = itemAbsLeft + angleAdjustedRelativeX;
    let loadingAbsTop = itemAbsTop + angleAdjustedRelativeY;

    const loadingAbsRight = loadingAbsLeft + actualLoadingWidth;
    const loadingAbsBottom = loadingAbsTop + loadingHeightCanvas;
    const fullCanvasWidth = item.page.poster.width;
    const fullCanvasHeight = item.page.poster.height;

    // Show the loading at the top left if the bottom right corner would be off-canvas
    if (loadingAbsRight > fullCanvasWidth - LOADING_INDICATOR_POSITION_BUFFER || loadingAbsBottom > fullCanvasHeight - LOADING_INDICATOR_POSITION_BUFFER) {
      relativeX = margin;
      relativeY = margin;
      angleAdjustedRelativeX = relativeX * Math.cos(angleRadians) - relativeY * Math.sin(angleRadians);
      angleAdjustedRelativeY = relativeX * Math.sin(angleRadians) + relativeY * Math.cos(angleRadians);
      loadingAbsLeft = itemAbsLeft + angleAdjustedRelativeX;
      loadingAbsTop = itemAbsTop + angleAdjustedRelativeY;
    }

    // Convert canvas coordinates to viewport coordinates by multiplying by posterScale
    return {
      left: loadingAbsLeft * posterScale,
      top: loadingAbsTop * posterScale,
      angle: itemAbsoluteAngle,
    };
  };

  const loadingText = getLoadingText(item, loadingState);
  const position = getLoadingPosition(item, loadingText);
  if (!position) {
    return null;
  }

  const width = getLoadingWidthViewport(loadingText);

  return {
    loadingText,
    position,
    width,
  };
}
